import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/common/Card';
import { Button } from '@/components/common/Button';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { apiClient } from '@/services/api';
import toast from 'react-hot-toast';
import { RefreshCw, Database, CheckCircle, XCircle } from 'lucide-react';

interface AuthStatus {
  authenticated: boolean;
  expiresAt?: string;
}

interface CodeCounts {
  pfpCodes: number;
  contributionCodes: number;
  locationCodes: number;
  bankAccounts: number;
  departmentCodes: number;
  projectCodes: number;
  classCodes: number;
  accounts: number;
}

export function SagePage() {
  const queryClient = useQueryClient();
  const [syncInProgress, setSyncInProgress] = useState(false);

  // Fetch auth status
  const { data: authData, isLoading: authLoading } = useQuery({
    queryKey: ['sageAuthStatus'],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: AuthStatus }>('/api/sage/auth/status');
      return response.data.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch code counts
  const { data: countsData, isLoading: countsLoading, refetch: refetchCounts } = useQuery({
    queryKey: ['sageCodeCounts'],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: CodeCounts }>('/api/sage/codes/counts');
      return response.data.data;
    },
  });

  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: async () => {
      setSyncInProgress(true);
      const response = await apiClient.post<{ success: boolean; message: string; data?: { counts: CodeCounts } }>('/api/sage/codes/sync');
      return response.data;
    },
    onSuccess: (result) => {
      setSyncInProgress(false);
      if (result.success) {
        toast.success(result.message || 'Cache tables synced successfully!');
        queryClient.invalidateQueries({ queryKey: ['sageCodeCounts'] });
        queryClient.invalidateQueries({ queryKey: ['sageAuthStatus'] });
        queryClient.invalidateQueries({ queryKey: ['dimensionCodes'] });
        refetchCounts();
      } else {
        toast.error(result.message || 'Failed to sync cache tables');
      }
    },
    onError: (error: unknown) => {
      setSyncInProgress(false);
      // Extract error message from axios error response
      const axiosError = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = axiosError.response?.data?.message || axiosError.message || 'Unknown error occurred';
      toast.error(`Sync failed: ${errorMessage}`);
    },
  });

  const authStatus = authData;
  const counts = countsData;
  const isAuthenticated = authStatus?.authenticated ?? false;

  const totalCodes = counts ? 
    counts.pfpCodes + counts.contributionCodes + counts.locationCodes + 
    counts.bankAccounts + counts.departmentCodes + counts.projectCodes + 
    counts.classCodes + counts.accounts : 0;

  const handleSync = () => {
    syncMutation.mutate();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Sage Intacct Integration
        </h1>
      </div>

      {/* Authentication Status */}
      <Card title="Connection Status">
        {authLoading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex items-center space-x-3">
            {isAuthenticated ? (
              <>
                <CheckCircle className="h-6 w-6 text-green-500" />
                <div>
                  <span className="text-green-600 dark:text-green-400 font-medium">
                    Connected to Sage Intacct
                  </span>
                  {authStatus?.expiresAt && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Token expires: {new Date(authStatus.expiresAt).toLocaleString()}
                    </p>
                  )}
                </div>
              </>
            ) : (
              <>
                <XCircle className="h-6 w-6 text-yellow-500" />
                <div>
                  <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                    Not connected
                  </span>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Will auto-connect when syncing cache
                  </p>
                </div>
              </>
            )}
          </div>
        )}
      </Card>

      {/* Cache Tables */}
      <Card title="Dimension Code Cache">
        <div className="space-y-6">
          {/* Sync Button */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                Refresh Cache Tables
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Sync dimension codes from Sage Intacct to the local database cache.
                This populates the dropdowns in the Journal Posting page.
              </p>
            </div>
            <Button 
              onClick={handleSync} 
              variant="primary"
              disabled={syncInProgress}
              isLoading={syncInProgress}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncInProgress ? 'animate-spin' : ''}`} />
              {syncInProgress ? 'Syncing...' : 'Refresh Cache'}
            </Button>
          </div>

          {/* Code Counts Table */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center">
              <Database className="h-4 w-4 mr-2" />
              Cached Code Counts
            </h4>
            
            {countsLoading ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <CountCard label="PFP Codes" count={counts?.pfpCodes ?? 0} />
                <CountCard label="Contribution Codes" count={counts?.contributionCodes ?? 0} />
                <CountCard label="Location Codes" count={counts?.locationCodes ?? 0} />
                <CountCard label="Bank Accounts" count={counts?.bankAccounts ?? 0} />
                <CountCard label="Department Codes" count={counts?.departmentCodes ?? 0} />
                <CountCard label="Project Codes" count={counts?.projectCodes ?? 0} />
                <CountCard label="Class Codes" count={counts?.classCodes ?? 0} />
                <CountCard label="GL Accounts" count={counts?.accounts ?? 0} />
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Total cached codes: <span className="font-semibold">{totalCodes.toLocaleString()}</span>
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Instructions */}
      <Card title="How It Works">
        <div className="prose dark:prose-invert max-w-none text-sm">
          <ol className="list-decimal list-inside space-y-2 text-gray-600 dark:text-gray-400">
            <li>
              <strong>Refresh Cache</strong> - Click "Refresh Cache" to fetch dimension codes from Sage Intacct 
              and store them in the local SQLite database. Authentication happens automatically.
            </li>
            <li>
              <strong>Use in Journal Posting</strong> - The cached codes populate the dropdown menus 
              (Department, Project, Class, PFP, Contribution, etc.) in the Journal Posting page.
            </li>
          </ol>
          <p className="mt-4 text-gray-500 dark:text-gray-500">
            The cache is stored locally and persists between server restarts. 
            Refresh periodically to keep codes up-to-date with Sage Intacct.
          </p>
        </div>
      </Card>
    </div>
  );
}

// Helper component for count cards
function CountCard({ label, count }: { label: string; count: number }) {
  return (
    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
        {count.toLocaleString()}
      </p>
    </div>
  );
}
